import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApiQuery, useInvalidateQueries } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  KeywordGroup,
  KeywordListParams,
  KeywordListResponse,
  UseKeywordListReturn,
} from '../types/keywordTypes';

const ITEMS_PER_PAGE = 30;

const fetchKeywordList = async (
  params: KeywordListParams,
): Promise<ApiResponse<KeywordListResponse>> => {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
  });

  if (params.keyword && params.keyword.trim()) {
    searchParams.append('keyword', params.keyword.trim());
  }

  return api.get<KeywordListResponse>(
    `/api/v1/keyword/list?${searchParams.toString()}`,
  );
};

export const useKeywordList = (): UseKeywordListReturn => {
  const location = useLocation();
  const navigate = useNavigate();
  const { invalidateQueries } = useInvalidateQueries();

  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [shouldReset, setShouldReset] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedKeywordGroupId, setSelectedKeywordGroupId] = useState<
    number | null
  >(null);

  // URL에서 파라미터 초기화
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('page');
    const keywordParam = searchParams.get('keyword');

    const page = pageParam ? parseInt(pageParam, 10) : 1;
    const searchKeyword = keywordParam || '';

    setCurrentPage(page);
    setKeyword(searchKeyword);

    if (!pageParam) {
      searchParams.set('page', '1');
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location, navigate]);

  // 키워드 목록 조회
  const {
    data: keywordListData,
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    ['keywordList', currentPage, keyword],
    () =>
      fetchKeywordList({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        keyword,
      }),
    {
      enabled: true,
    },
  );

  const updateURL = useCallback(
    (page: number, searchKeyword: string) => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', page.toString());

      if (searchKeyword && searchKeyword.trim()) {
        searchParams.set('keyword', searchKeyword.trim());
      }

      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [location.pathname, navigate],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      updateURL(page, keyword);
    },
    [keyword, updateURL],
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      setKeyword(searchTerm);
      setCurrentPage(1);
      updateURL(1, searchTerm);
    },
    [updateURL],
  );

  const handleReset = useCallback(() => {
    setKeyword('');
    setCurrentPage(1);
    setShouldReset(true);
    updateURL(1, '');
  }, [updateURL]);

  const handleResetComplete = useCallback(() => {
    setShouldReset(false);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleGroupClick = useCallback(
    (group: KeywordGroup) => {
      // 키워드 상세 캐시 무효화 (항상 최신 데이터 가져오도록)
      invalidateQueries(['keywordDetail', group.id]);
      setSelectedKeywordGroupId(group.id);
      setIsModalOpen(true);
    },
    [invalidateQueries],
  );

  const handleOpenModal = useCallback(() => {
    setSelectedKeywordGroupId(null);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedKeywordGroupId(null);
    // 목록 새로고침
    refetch();
  }, [refetch]);

  // 계산된 값들
  const keywordGroups = keywordListData?.data?.keywordGroupList || [];
  const totalCount = keywordListData?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const hasError = !!error;
  const isEmpty = !isLoading && !hasError && keywordGroups.length === 0;

  return {
    keywordGroups,
    currentPage,
    totalPages,
    keyword,
    isLoading,
    hasError,
    isEmpty,
    shouldReset,
    totalCount,
    isModalOpen,
    selectedKeywordGroupId,
    handlePageChange,
    handleSearch,
    handleReset,
    handleResetComplete,
    handleRefresh,
    handleGroupClick,
    handleOpenModal,
    handleCloseModal,
    error,
  };
};
